import React, {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { FormApi, FormContext, Validators } from './types';

// The props of the internal FormBuilder component.
interface FormBuilderProps<T> {
  children: React.ReactNode;
  initialFormData: T;
  setFormApi: React.Dispatch<React.SetStateAction<FormApi<T>>>;
  validators: Validators<T>;
  onSubmit: () => void;
}

// The internal FormBuilder component that contains the core form implementation.
function FormBuilder<T>({
  children,
  setFormApi,
  validators,
  onSubmit,
  initialFormData,
}: FormBuilderProps<T>): React.ReactElement<FormBuilderProps<T>> {
  const [formFields, setFormFields] = useState(new Set<keyof T>());
  const [formData, setFormData] = useState<Partial<T>>({});
  const [formErrors, setFormErrors] = useState<
    Partial<Record<keyof T, string>>
  >({});

  useEffect(() => {
    setFormData((data) => ({
      ...data,
      ...initialFormData,
    }));
  }, [initialFormData]);

  useEffect(() => {
    setFormErrors(
      Object.keys(validators)
        .filter((key) => formFields.has(key as keyof T))
        .reduce((acc, field) => {
          const error = validators[field]?.(formData[field]);
          if (error) {
            acc[field] = error;
          }
          return acc;
        }, {}),
    );
  }, [formData, validators, formFields]);

  const registerField = useMemo(
    () => (field: keyof T) => {
      setFormFields((fields) => new Set([...fields, field]));
    },
    [],
  );
  const unregisterField = useMemo(
    () => (field: keyof T) => {
      setFormFields((fields) => new Set([...fields, field]));
    },
    [],
  );

  const formApi = useMemo(() => {
    return {
      formData,
      formErrors,
      setFormData,
      validators,
      register: registerField,
      unregister: unregisterField,
    };
  }, [
    setFormData,
    formData,
    formErrors,
    validators,
    registerField,
    unregisterField,
  ]);

  useEffect(() => setFormApi(formApi), [setFormApi, formApi]);

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      onSubmit();
    },
    [onSubmit],
  );

  return (
    <FormContext.Provider value={formApi}>
      <form onSubmit={handleSubmit}>{children}</form>
    </FormContext.Provider>
  );
}

export default FormBuilder;
